// src/components/AudioFilters.tsx

import React, { useState, useEffect, useRef } from 'react';
import {
  AudioFilters as AudioFiltersType,
  AudioFocus,
  AgeGroup,
  BiologicalSex,
  MurmurType,
  Outcome,
  MurmurGrading,
  MurmurTiming,
  MurmurShape,
  MurmurPitch,
  MurmurQuality,
  AudioFocusLabels,
  MurmurTimingLabels,
  MurmurShapeLabels,
  MurmurPitchLabels,
  MurmurQualityLabels
} from '../types/audio.types';

interface AudioFiltersProps {
  filters: AudioFiltersType;
  onFiltersChange: (filters: AudioFiltersType) => void;
  onClear: () => void;
}

const AudioFilters: React.FC<AudioFiltersProps> = ({ filters, onFiltersChange, onClear }) => {
  // Estados locais apenas para campos de texto com debounce
  const [textInputs, setTextInputs] = useState({
    subject_id: filters.subject_id || '',
    murmur_locations: filters.murmur_locations || ''
  });

  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Atualiza estados locais quando filtros externos mudam (ex: limpar filtros)
  useEffect(() => {
    setTextInputs({
      subject_id: filters.subject_id || '',
      murmur_locations: filters.murmur_locations || ''
    });
  }, [filters.subject_id, filters.murmur_locations]);

  // Cleanup do timer ao desmontar
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Aplica filtros imediatamente (para checkboxes)
  const applyFilter = (key: keyof AudioFiltersType, value: any) => {
    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
  };

  // Aplica filtros com debounce (para campos de texto)
  const applyFilterDebounced = (key: keyof AudioFiltersType, value: any) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const newFilters = { ...filters, [key]: value };
      onFiltersChange(newFilters);
    }, 500);
  };

  const toggleCheckbox = (key: keyof AudioFiltersType, value: string) => {
    // Tratamento especial para pregnancy_status (boolean)
    if (key === 'pregnancy_status') {
      const boolValue = value === 'true';
      const newValue = filters.pregnancy_status === boolValue ? undefined : boolValue;
      applyFilter(key, newValue);
      return;
    }

    // Para outros campos (string arrays)
    const currentArray = (filters[key] as string[]) || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    applyFilter(key, newArray.length > 0 ? newArray : undefined);
  };

  const handleTextInputChange = (key: 'subject_id' | 'murmur_locations', value: string) => {
    setTextInputs(prev => ({ ...prev, [key]: value }));
    applyFilterDebounced(key, value || undefined);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Coluna 1 */}
      <div className="space-y-4">
        {/* Foco Cardíaco */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-gray-700">
              Foco Cardíaco
            </label>
          </div>
          <div className="space-y-1.5">
            {Object.values(AudioFocus).map(focus => (
              <label key={focus} className="flex items-center space-x-2 text-xs hover:bg-gray-50 p-1 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={(filters.focus || []).includes(focus)}
                  onChange={() => toggleCheckbox('focus', focus)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span className="text-gray-700">{AudioFocusLabels[focus]}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Faixa Etária */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Faixa Etária
          </label>
          <div className="space-y-1.5">
            {Object.values(AgeGroup).map(age => (
              <label key={age} className="flex items-center space-x-2 text-xs hover:bg-gray-50 p-1 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={(filters.age_group || []).includes(age)}
                  onChange={() => toggleCheckbox('age_group', age)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span className="text-gray-700">{age}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Sexo Biológico */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Sexo Biológico
          </label>
          <div className="space-y-1.5">
            {Object.values(BiologicalSex).map(sex => (
              <label key={sex} className="flex items-center space-x-2 text-xs hover:bg-gray-50 p-1 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={(filters.biological_sex || []).includes(sex)}
                  onChange={() => toggleCheckbox('biological_sex', sex)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span className="text-gray-700">{sex}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Coluna 2 */}
      <div className="space-y-4">
        {/* Presença de Murmúrio */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Presença de Murmúrio
          </label>
          <div className="space-y-1.5">
            {Object.values(MurmurType).map(murmur => (
              <label key={murmur} className="flex items-center space-x-2 text-xs hover:bg-gray-50 p-1 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={(filters.murmur || []).includes(murmur)}
                  onChange={() => toggleCheckbox('murmur', murmur)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span className="text-gray-700">{murmur}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Outcome */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Resultado (Outcome)
          </label>
          <div className="space-y-1.5">
            {Object.values(Outcome).map(outcome => (
              <label key={outcome} className="flex items-center space-x-2 text-xs hover:bg-gray-50 p-1 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={(filters.outcome || []).includes(outcome)}
                  onChange={() => toggleCheckbox('outcome', outcome)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span
                  className={`font-medium ${
                    outcome === 'Normal' ? 'text-green-600' :
                    outcome === 'Abnormal' ? 'text-red-600' : 'text-gray-700'
                  }`}
                >
                  {outcome}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Localizações do Murmúrio + Mais Audível + Gravidez + Campos Avançados (sist/diast) */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
          {/* ID do Paciente (pequeno e direto) */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              ID do Paciente
            </label>
            <input
              type="text"
              value={textInputs.subject_id}
              onChange={(e) => handleTextInputChange('subject_id', e.target.value)}
              placeholder="Ex: 50001"
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs"
            />
          </div>

          {/* Localizações do Murmúrio */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Localizações do Murmúrio
            </label>
            <input
              type="text"
              value={textInputs.murmur_locations}
              onChange={(e) => handleTextInputChange('murmur_locations', e.target.value)}
              placeholder="Ex: AV+MV ou TV"
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Digite as siglas (AV, PV, MV, TV, Phc)
            </p>
          </div>

          {/* Localização Mais Audível */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Localização Mais Audível
            </label>
            <div className="flex flex-wrap gap-2">
              {['AV', 'PV', 'MV', 'TV', 'Phc'].map(location => (
                <label key={location} className="inline-flex items-center space-x-2 text-xs p-1 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(filters.most_audible_location || []).includes(location)}
                    onChange={() => toggleCheckbox('most_audible_location', location)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                  />
                  <span className="text-gray-700">{location}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Gravidez */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Status de Gravidez
            </label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.pregnancy_status === true}
                  onChange={() => toggleCheckbox('pregnancy_status', 'true')}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span className="text-gray-700">Grávida</span>
              </label>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.pregnancy_status === false}
                  onChange={() => toggleCheckbox('pregnancy_status', 'false')}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                />
                <span className="text-gray-700">Não Grávida</span>
              </label>
            </div>
          </div>

          {/* Avançados resumidos: timing/shape/pitch/quality (sistólico + diastólico) */}
          <details className="rounded border border-gray-200">
            <summary className="text-xs font-semibold px-3 py-2 cursor-pointer select-none">
              Opções avançadas de murmúrio
            </summary>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Sistólico - Timing */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Sistólico — Timing
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurTiming).filter(t => t.includes('systolic')).map(timing => (
                    <label key={timing} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.systolic_murmur_timing || []).includes(timing)}
                        onChange={() => toggleCheckbox('systolic_murmur_timing', timing)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurTimingLabels[timing]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sistólico - Forma */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Sistólico — Forma
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurShape).map(shape => (
                    <label key={shape} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.systolic_murmur_shape || []).includes(shape)}
                        onChange={() => toggleCheckbox('systolic_murmur_shape', shape)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurShapeLabels[shape]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sistólico - Grau */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Sistólico — Grau
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurGrading).filter(g => g.includes('VI')).map(grading => (
                    <label key={grading} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.systolic_murmur_grading || []).includes(grading)}
                        onChange={() => toggleCheckbox('systolic_murmur_grading', grading)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{grading}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sistólico — Tom */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Sistólico — Tom
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurPitch).map(pitch => (
                    <label key={pitch} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.systolic_murmur_pitch || []).includes(pitch)}
                        onChange={() => toggleCheckbox('systolic_murmur_pitch', pitch)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurPitchLabels[pitch]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sistólico — Qualidade */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Sistólico — Qualidade
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurQuality).map(quality => (
                    <label key={quality} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.systolic_murmur_quality || []).includes(quality)}
                        onChange={() => toggleCheckbox('systolic_murmur_quality', quality)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurQualityLabels[quality]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Diastólico — Timing */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Diastólico — Timing
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurTiming).filter(t => t.includes('diastolic')).map(timing => (
                    <label key={timing} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.diastolic_murmur_timing || []).includes(timing)}
                        onChange={() => toggleCheckbox('diastolic_murmur_timing', timing)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurTimingLabels[timing]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Diastólico — Forma */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Diastólico — Forma
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurShape).map(shape => (
                    <label key={shape} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.diastolic_murmur_shape || []).includes(shape)}
                        onChange={() => toggleCheckbox('diastolic_murmur_shape', shape)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurShapeLabels[shape]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Diastólico — Grau */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Diastólico — Grau
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurGrading).filter(g => g.includes('IV')).map(grading => (
                    <label key={grading} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.diastolic_murmur_grading || []).includes(grading)}
                        onChange={() => toggleCheckbox('diastolic_murmur_grading', grading)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{grading}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Diastólico — Tom */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Diastólico — Tom
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurPitch).map(pitch => (
                    <label key={pitch} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.diastolic_murmur_pitch || []).includes(pitch)}
                        onChange={() => toggleCheckbox('diastolic_murmur_pitch', pitch)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurPitchLabels[pitch]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Diastólico — Qualidade */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Diastólico — Qualidade
                </label>
                <div className="space-y-1.5">
                  {Object.values(MurmurQuality).map(quality => (
                    <label key={quality} className="flex items-center gap-2 text-[11px] hover:bg-gray-50 p-1 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(filters.diastolic_murmur_quality || []).includes(quality)}
                        onChange={() => toggleCheckbox('diastolic_murmur_quality', quality)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>{MurmurQualityLabels[quality]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default AudioFilters;
